//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import ErrorCircle from 'assets/exclamation-x-circle.svg';
import { AxiosError, AxiosResponse } from 'axios';
import { Buffer } from 'buffer';
import { useAuth } from 'contexts/AuthProvider';
import { useConfig } from 'contexts/ConfigProvider';
import { useLoadingMask } from 'contexts/LoadingMaskProvider';
import { useToast } from 'contexts/ToastProvider';
import { RttyAuthorized } from 'models/rtty-authorized';
import { RttyFontSize } from 'models/rtty-fontsize';
import { RttyInfo } from 'models/rtty-info';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { IDisposable, Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import 'xterm/css/xterm.css';
import OverlayAddon from './xterm-addon-overlay';

const LoginErrorOffline = 0x01;
const LoginErrorBusy = 0x02;
const AckBlkSize = 4 * 1024;

const Rtty = () => {
  const { t } = useTranslation();
  const { serialNumber } = useParams();
  const { getRttyBySerialNumber, getRttyAuthorized, getRttyFontSize } = useAuth();
  const { addLoadingMask } = useLoadingMask();
  const { addToast } = useToast();
  const { cbtApiUrl, developmentRttyUrl } = useConfig();

  const rttyInfoRef = useRef<RttyInfo>({} as RttyInfo);
  const socketRef = useRef<WebSocket | null>(null);
  const terminalRef = useRef<HTMLDivElement>(null);
  const initialTimeoutRef = useRef<NodeJS.Timeout>();
  const resizeDelayTimeoutRef = useRef<NodeJS.Timeout>();
  const termRef = useRef<Terminal>();
  const fitAddonRef = useRef<FitAddon>();
  const disposablesRef = useRef<IDisposable[]>([]);
  const unackRef = useRef<number>(0);

  const getConnectionId = (serialNumber: string) => {
    addLoadingMask({
      show: true,
    });
    getRttyBySerialNumber(serialNumber)
      .then((response: AxiosResponse<RttyInfo>) => {
        if (response.data.connectionId) {
          rttyInfoRef.current = response.data;
          getAuthorized();
        } else {
          addToast({
            title: t('toast.failedToObtainConnectionId'),
            message: '',
            color: 'bg-toast-error-50',
            icon: ErrorCircle,
            text: 'text-toast-error-800',
            svg: 'toastIcon-error',
          });
        }
      })
      .catch((error: AxiosError<{ statusCode: number; message: string }>) => {
        addToast({
          title: t('toast.connectionFailed'),
          message: error?.response?.data?.message ?? '',
          color: 'bg-toast-error-50',
          icon: ErrorCircle,
          text: 'text-toast-error-800',
          svg: 'toastIcon-error',
        });
      })
      .finally(() => {
        addLoadingMask({
          show: false,
        });
      });
  };

  const getAuthorized = () => {
    getRttyAuthorized(rttyInfoRef.current.connectionId!)
      .then((response: AxiosResponse<RttyAuthorized>) => {
        if (response.data.authorized) {
          initialTerminal();
        } else {
          addToast({
            title: t('toast.authorizedFailed'),
            message: '',
            color: 'bg-toast-error-50',
            icon: ErrorCircle,
            text: 'text-toast-error-800',
            svg: 'toastIcon-error',
          });
        }
      })
      .catch(() => {
        addToast({
          title: t('toast.authorizedFailed'),
          message: '',
          color: 'bg-toast-error-50',
          icon: ErrorCircle,
          text: 'text-toast-error-800',
          svg: 'toastIcon-error',
        });
      });
  };

  const getFontSize = () => {
    getRttyFontSize(rttyInfoRef.current.connectionId!).then((response: AxiosResponse<RttyFontSize>) => {
      // termRef.current.setOption('fontSize', response.data.size);
      fitTerm();
    });
  };

  useEffect(() => {
    return () => {
      dispose();
      termRef.current?.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (serialNumber && cbtApiUrl !== undefined) {
      initialTimeoutRef.current = setTimeout(() => {
        getConnectionId(serialNumber);
      }, 500);
    }

    return () => {
      if (initialTimeoutRef.current) clearTimeout(initialTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialNumber, cbtApiUrl]);

  const sendTermData = (data: string) => {
    socketRef.current!.send(Buffer.concat([Buffer.from([0]), Buffer.from(data)]));
  };

  const dispose = () => {
    disposablesRef.current.forEach((d: IDisposable) => d.dispose());
  };

  const closed = () => {
    termRef.current?.write('\n\n\r\x1B[1;3;31mConnection is closed.\x1B[0m');
    dispose();
  };

  const fitTerm = () => {
    if (resizeDelayTimeoutRef.current) clearTimeout(resizeDelayTimeoutRef.current);
    resizeDelayTimeoutRef.current = setTimeout(() => {
      fitAddonRef.current?.fit();
    }, 200);
  };

  const openTerm = () => {
    const term = new Terminal({
      cursorBlink: true,
      fontSize: 16,
    });
    termRef.current = term;

    const fitAddon = new FitAddon();
    fitAddonRef.current = fitAddon;
    term.loadAddon(fitAddon);

    const overlayAddon = new OverlayAddon();
    term.loadAddon(overlayAddon);

    term.open(terminalRef.current!);
    term.focus();

    disposablesRef.current.push(term.onData((data) => sendTermData(data)));
    disposablesRef.current.push(term.onBinary((data) => sendTermData(data)));

    disposablesRef.current.push(
      term.onResize((size) => {
        const msg = { type: 'winsize', cols: size.cols, rows: size.rows };
        socketRef.current!.send(JSON.stringify(msg));
        overlayAddon.show(term.cols + 'x' + term.rows);
      })
    );

    window.addEventListener('resize', fitTerm);

    disposablesRef.current.push({
      dispose: () => window.removeEventListener('resize', fitTerm),
    } as IDisposable);
  };
  const retryCount = useRef(0);
  const maxRetries = 5;
  const retryDelay = 2000; // 2 seconds

  const initialTerminal = () => {
    if (retryCount.current >= maxRetries) {
      addToast({
        title: t('toast.deviceOffline'),
        message: '',
        color: 'bg-toast-error-50',
        icon: ErrorCircle,
        text: 'text-toast-error-800',
        svg: 'toastIcon-error',
      });
      return;
    }
    let protocol = window.location.protocol;
    let host = window.location.host;

    if (process.env.NODE_ENV === 'development') {
      protocol = developmentRttyUrl;
      host = protocol.replace(new RegExp('^https://'), '');
    }

    protocol = protocol.indexOf('https:') === 0 ? 'wss://' : 'ws://';

    const socket = new WebSocket(protocol + host + `/owgw/rtty/connect/${rttyInfoRef.current.connectionId}`);
    socket.binaryType = 'arraybuffer';
    socketRef.current = socket;

    socket.addEventListener('message', (ev: { data: WithImplicitCoercion<ArrayBuffer | SharedArrayBuffer> }) => {
      const data = ev.data;

      if (typeof data === 'string') {
        const msg = JSON.parse(data);
        if (msg.type === 'login') {
          if (msg.err === LoginErrorOffline) {
            socket.close();
            retryCount.current += 1;
            setTimeout(() => {
              initialTerminal();
            }, retryDelay);
            return;
          } else if (msg.err === LoginErrorBusy) {
            addToast({
              title: t('toast.sessionTimedOut'),
              message: t('toast.sessionTimedOutMessage'),
              color: 'bg-toast-error-50',
              icon: ErrorCircle,
              text: 'text-toast-error-800',
              svg: 'toastIcon-error',
            });
            return;
          }

          openTerm();

          getFontSize();
          retryCount.current = 0;
          socket.addEventListener('close', () => closed());
          socket.addEventListener('error', () => closed());
        }
      } else {
        const data = Buffer.from(ev.data);

        unackRef.current += data.length;
        termRef.current?.write(typeof data === 'string' ? data : new Uint8Array(data));

        if (unackRef.current > AckBlkSize) {
          const msg = { type: 'ack', ack: unackRef.current };
          socket.send(JSON.stringify(msg));
          unackRef.current = 0;
        }
      }
    });
  };

  return <div ref={terminalRef} className="h-full"></div>;
};

export default Rtty;
